import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Alert,
  Snackbar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  PlayArrow as PlayIcon,
  Assignment as SummaryIcon,
  StorefrontOutlined as StorefrontIcon,
} from '@mui/icons-material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import ChatSimulation from '../components/automations/ChatSimulation';
import HandoffConfigEditor from '../components/automations/HandoffConfigEditor';
import handoffApi, { MasterField, HandoffField } from '../services/handoffApi';
import { canAddField, mapMasterToHandoff, MAX_FIELDS } from '../utils/handoffUtils';
import SMBSelector from '../components/SMBSelector';
import { useSMBContext } from '../contexts/SMBContext';
import PageBanner from '../components/PageBanner';
import EmptyState from '../components/EmptyState';
import { AutomationsSkeleton } from '../components/skeletons';
import { PAGE_BANNER_PRIMARY_BTN_SX, PAGE_BANNER_OUTLINED_BTN_SX } from '../theme/constants';

const Automations: React.FC = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext<{ smbNavState?: { isOnboarded: boolean; hasSubscription: boolean } } | null>()
  const hasSubscription = outletContext?.smbNavState?.hasSubscription
  const { smbs, selectedSMBId: smbId, setSelectedSMBId: setSmbId, loading: smbsLoading } = useSMBContext()
  const [masterFields, setMasterFields] = useState<MasterField[]>([]);
  const [selectedFields, setSelectedFields] = useState<HandoffField[]>([]);
  const [initialFields, setInitialFields] = useState<HandoffField[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempSelection, setTempSelection] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [maxFields, setMaxFields] = useState<number>(MAX_FIELDS);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewInitialView, setPreviewInitialView] = useState<'chat' | 'summary'>('chat');

  // Deep comparison to detect changes
  const hasChanges = JSON.stringify(selectedFields) !== JSON.stringify(initialFields);

  useEffect(() => {
    if (!smbId) return;
    const loadData = async () => {
      try {
        setLoading(true);
        const [master, config] = await Promise.all([
          handoffApi.fetchMasterFields(),
          handoffApi.fetchHandoffConfig(smbId),
        ]);
        const fields = config.fields || [];
        setMasterFields(master.fields || []);
        setSelectedFields(fields);
        setInitialFields(fields);
        if (master.max_fields) setMaxFields(master.max_fields);
        else if (config.max_fields) setMaxFields(config.max_fields);
      } catch (err) {
        console.error('Failed to load handoff data:', err);
        setError('Failed to load configuration. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [smbId]);

  const handleOpenModal = () => {
    setTempSelection([]);
    setIsModalOpen(true);
  };

  const handleToggleField = (id: string) => {
    if (selectedFields.some((f) => f.id === id)) return;
    const isSelected = tempSelection.includes(id);
    const remainingSlots = maxFields - selectedFields.length;

    if (!isSelected && tempSelection.length >= remainingSlots) {
      setError(`You can only select up to ${remainingSlots} more question(s).`);
      return;
    }

    setTempSelection((prev) =>
      isSelected ? prev.filter((fieldId) => fieldId !== id) : [...prev, id]
    );
  };

  const handleAddFields = () => {
    const newFields: HandoffField[] = [];
    let currentOrder = selectedFields.length + 1;

    tempSelection.forEach((id) => {
      const master = masterFields.find((m) => m.id === id);
      if (master && !selectedFields.find((f) => f.id === id)) {
        const { canAdd, error: validationError } = canAddField(
          [...selectedFields, ...newFields],
          id,
          maxFields
        );
        if (canAdd) {
          newFields.push(mapMasterToHandoff(master, currentOrder++));
        } else {
          setError(validationError || 'Cannot add field');
        }
      }
    });

    setSelectedFields([...selectedFields, ...newFields].map((f, i) => ({ ...f, order: i + 1 })));
    setIsModalOpen(false);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSave = async () => {
    // Validate all fields
    const invalidFields = selectedFields.filter(
      (f) => !f.label.trim() || !f.description.trim() || !f.example.trim()
    );

    if (invalidFields.length > 0) {
      setShowValidationErrors(true);
      setError(
        'Some questions are missing details. Please fill in the highlighted areas before saving.'
      );
      return;
    }

    const hasRequiredField = selectedFields.some((f) => f.required);
    if (!hasRequiredField && selectedFields.length > 0) {
      setError('At least one question must be marked as "Must Ask?" to save changes.');
      return;
    }

    try {
      setSaving(true);
      setShowValidationErrors(false);
      await handoffApi.saveHandoffConfig(smbId, { fields: selectedFields });
      setInitialFields(selectedFields);
      setSuccessMessage('Configuration saved successfully!');
    } catch (err) {
      console.error('Failed to save handoff config:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageBanner
        title="Connect to Team"
        subtitle={isMobile ? "Chatbot lead qualification questions" : "These questions will be asked by the chatbot for your team to contact later."}
        action={
          <>
            <Button
              variant="contained"
              fullWidth={isMobile}
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              disabled={loading || smbsLoading || !smbId || selectedFields.length >= maxFields}
              sx={PAGE_BANNER_PRIMARY_BTN_SX}
            >
              {isMobile ? 'Questions' : 'Add Questions'}
            </Button>
            <Button
              variant={hasChanges ? "contained" : "outlined"}
              fullWidth={isMobile}
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || !hasChanges || !smbId || loading || smbsLoading}
              sx={hasChanges ? PAGE_BANNER_PRIMARY_BTN_SX : PAGE_BANNER_OUTLINED_BTN_SX}
            >
              {saving ? 'Saving...' : isMobile ? 'Save' : 'Save Questions'}
            </Button>
          </>
        }
        onRefresh={() => {
          if (!smbId) return;
          setIsRefreshing(true);
          Promise.all([
            handoffApi.fetchMasterFields(),
            handoffApi.fetchHandoffConfig(smbId),
          ]).then(([master, config]) => {
            const fields = config.fields || [];
            setMasterFields(master.fields || []);
            setSelectedFields(fields);
            setInitialFields(fields);
            if (master.max_fields) setMaxFields(master.max_fields);
            else if (config.max_fields) setMaxFields(config.max_fields);
          }).finally(() => setIsRefreshing(false));
        }}
        refreshing={isRefreshing}
      />

      {/* SMB Selection */}
      <SMBSelector
        smbs={smbs}
        selectedSMBId={smbId}
        loading={smbsLoading}
        onChange={(value) => setSmbId(value)}
      />



      {smbsLoading || (loading && !isRefreshing && smbId) ? (
        <AutomationsSkeleton />
      ) : (smbs.length === 0 || (smbId && !smbs.find((s) => s.id === smbId)?.onboarding_completed)) ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, bgcolor: 'background.paper', borderColor: 'divider' }}>
          {hasSubscription === false ? (
            <EmptyState
              icon={<StorefrontIcon />}
              title="Subscription Required"
              description="An active plan with AI Replies is required to access Automations. Choose a plan to set up custom questions for your AI agent to automatically qualify leads."
              action={
                <Button variant="contained" onClick={() => navigate('/dashboard')}>
                  Choose Plan
                </Button>
              }
              minHeight={360}
            />
          ) : (
            <EmptyState
              icon={<StorefrontIcon />}
              title="Add a Business to Get Started"
              description="You haven't added a business yet. Connect your WhatsApp business to configure AI customer questions."
              action={
                <Button variant="contained" onClick={() => navigate('/dashboard')}>
                  Add Business
                </Button>
              }
              minHeight={360}
            />
          )}
        </Paper>
      ) : !smbId ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, bgcolor: 'background.paper', borderColor: 'divider' }}>
          <EmptyState
            icon={<StorefrontIcon />}
            title="No Business Selected"
            description="Select a business from the dropdown above to configure customer questions and AI automation settings."
            minHeight={360}
          />
        </Paper>
      ) : (
      <>
      <Box sx={{ mb: 3 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          mb: 2,
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
            <Typography variant="h6" fontWeight="bold">What to Ask the Customer</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ bgcolor: 'action.hover', px: 1.5, py: 0.5, borderRadius: 10, fontWeight: 'medium' }}>
              Max {maxFields} | {selectedFields.length} Added
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
            {!isMobile && (
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PlayIcon />}
                  onClick={() => {
                    setPreviewInitialView('chat');
                    setIsPreviewOpen(true);
                  }}
                  disabled={selectedFields.length === 0}
                >
                  Chat Demo
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<SummaryIcon />}
                  onClick={() => {
                    setPreviewInitialView('summary');
                    setIsPreviewOpen(true);
                  }}
                  disabled={selectedFields.length === 0}
                >
                  View Responses
                </Button>
              </>
            )}
          </Box>
        </Box>

        <HandoffConfigEditor
          fields={selectedFields}
          showErrors={showValidationErrors}
          onFieldsChange={(newFields) => {
            setSelectedFields(newFields);
            if (showValidationErrors) {
              setShowValidationErrors(false);
            }
            if (error && (error.includes('Maximum of') || error.includes('missing details') || error.includes('Must Ask?'))) {
              setError(null);
            }
          }}
          onFocus={() => {
            if (showValidationErrors) {
              setShowValidationErrors(false);
            }
            if (error && error.includes('missing details')) {
              setError(null);
            }
          }}
        />

        {/* Mobile Action Buttons at Bottom */}
        {isMobile && (
          <Paper elevation={3} sx={{
            position: 'fixed',
            bottom: { xs: 'calc(env(safe-area-inset-bottom) + 68px)', md: 0 },
            left: 0,
            right: 0,
            p: 2,
            zIndex: 1000,
            borderRadius: 0,
            bgcolor: 'background.paper'
          }}>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  startIcon={<PlayIcon />}
                  onClick={() => {
                    setPreviewInitialView('chat');
                    setIsPreviewOpen(true);
                  }}
                  disabled={selectedFields.length === 0}
                  sx={{ borderRadius: 2 }}
                >
                  Chat Demo
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  fullWidth
                  startIcon={<SummaryIcon />}
                  onClick={() => {
                    setPreviewInitialView('summary');
                    setIsPreviewOpen(true);
                  }}
                  disabled={selectedFields.length === 0}
                  sx={{ borderRadius: 2 }}
                >
                  View Responses
                </Button>
              </Box>
            </Stack>
          </Paper>
        )}
      </Box>
      </>
      )}

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight="bold">Choose Questions to Ask</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>

            <Typography variant="caption" color={(selectedFields.length + tempSelection.length >= maxFields) ? 'error' : 'primary'} sx={{ fontWeight: 'bold' }}>
              You can pick up to {maxFields - selectedFields.length} more questions.
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {isMobile ? (
            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                {masterFields.map((field) => {
                  const alreadyAdded = selectedFields.some((f) => f.id === field.id);
                  return (
                    <Card
                      key={field.id}
                      variant="outlined"
                      sx={{
                        cursor: alreadyAdded ? 'default' : 'pointer',
                        opacity: alreadyAdded ? 0.6 : 1,
                        pointerEvents: alreadyAdded ? 'none' : 'auto',
                        borderColor: tempSelection.includes(field.id) ? 'primary.main' : 'divider',
                        bgcolor: alreadyAdded ? 'action.hover' : (tempSelection.includes(field.id) ? 'action.selected' : 'background.paper')
                      }}
                      onClick={() => handleToggleField(field.id)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Checkbox
                            size="small"
                            checked={alreadyAdded || tempSelection.includes(field.id)}
                            disabled={alreadyAdded}
                            sx={{ p: 0, mt: 0.5 }}
                          />
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                                {field.label}
                              </Typography>
                              <Typography variant="caption" sx={{ px: 1, bgcolor: 'action.hover', borderRadius: 1, textTransform: 'capitalize' }}>
                                {field.type}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {field.description}
                            </Typography>
                            {field.example && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 'bold' }}>
                                  Example : {field.example}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <Tooltip title="The query the chatbot will ask the customer" slotProps={{ tooltip: { sx: { fontSize: '0.875rem', py: 1, px: 1.5 } } }}>
                        <span style={{ cursor: 'help' }}>Question</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <Tooltip title="Explain what this question is asking and why it’s important for your team" slotProps={{ tooltip: { sx: { fontSize: '0.875rem', py: 1, px: 1.5 } } }}>
                        <span style={{ cursor: 'help' }}>Description</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <Tooltip title="The format of the response expected from the customer" slotProps={{ tooltip: { sx: { fontSize: '0.875rem', py: 1, px: 1.5 } } }}>
                        <span style={{ cursor: 'help' }}>Answer Type</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <Tooltip title="A sample answer to help the customer provide the right information" slotProps={{ tooltip: { sx: { fontSize: '0.875rem', py: 1, px: 1.5 } } }}>
                        <span style={{ cursor: 'help' }}>Example</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {masterFields.map((field) => {
                    const alreadyAdded = selectedFields.some((f) => f.id === field.id);
                    return (
                      <TableRow
                        key={field.id}
                        hover={!alreadyAdded}
                        onClick={() => handleToggleField(field.id)}
                        sx={{
                          cursor: alreadyAdded ? 'default' : 'pointer',
                          opacity: alreadyAdded ? 0.6 : 1,
                          bgcolor: alreadyAdded ? 'action.hover' : 'inherit',
                          pointerEvents: alreadyAdded ? 'none' : 'auto'
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={alreadyAdded || tempSelection.includes(field.id)}
                            disabled={alreadyAdded}
                            disableRipple
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'medium', textTransform: 'capitalize' }}>
                          {field.label}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {field.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ px: 1, py: 0.5, bgcolor: 'action.hover', borderRadius: 1, textTransform: 'capitalize' }}>
                            {field.type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {field.example || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: isMobile ? 'column-reverse' : 'row', gap: 1 }}>
          <Button fullWidth={isMobile} onClick={() => setIsModalOpen(false)}>Cancel</Button>
          <Tooltip
            title={(selectedFields.length + tempSelection.length > maxFields) ? `You can only add ${maxFields - selectedFields.length} more question(s).` : ''}
            placement="top"
          >
            <span>
              <Button
                fullWidth={isMobile}
                onClick={handleAddFields}
                variant="contained"
                disabled={tempSelection.length === 0 || (selectedFields.length + tempSelection.length > maxFields)}
              >
                Add {tempSelection.length} Question{tempSelection.length !== 1 ? 's' : ''}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <ChatSimulation
        fields={selectedFields}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        initialView={previewInitialView}
      />
    </Box>
  );
};

export default Automations;

